import {useForm} from '@mantine/form';
import {useQueryClient} from '@tanstack/react-query';
import {useState} from 'react';
import {useCreateEntryManagement} from '../../../core/repositories/mypageEntryManagementsRepository';
import type {AddMgtFormDataType} from '../components/Container/Presenter/Progress/ProgressMgtCreateDrawer';
import {ENTRY_SOURCE_TYPE_MANUAL} from '../constants/entrySourceTypes';
import {CACHE_KEYS} from './useEntryManagementUpdate';
import {validateProgressAddMgtForm} from './validateProgressAddMgtForm';

export const useProgressAddMgtActions = (
  existingCompanyIds: number[] = [],
  initialStartDate?: string,
  initialEndDate?: string,
) => {
  const queryClient = useQueryClient();
  const createMutation = useCreateEntryManagement();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useForm<AddMgtFormDataType>({
    initialValues: {
      companyId: 0,
      companyName: '',
      stepOrder: 1,
      stepName: '',
      deadline: undefined,
      notes: '',
      result: undefined,
      startDate: initialStartDate,
      endDate: initialEndDate,
      place: undefined,
    },
    validate: (values) => {
      const {errors} = validateProgressAddMgtForm(values, existingCompanyIds);
      return errors;
    },
  });

  const handleSubmit = async (): Promise<{
    success: boolean;
    stepID?: number;
    userEntryManagementID?: number;
  }> => {
    const validation = form.validate();
    if (validation.hasErrors) {
      return {success: false};
    }

    const formData = form.values;

    try {
      // POST /users/me/entry-managements でエントリー管理を新規作成
      const result = await createMutation.mutateAsync({
        companyID: formData.companyId,
        companyName: formData.companyName,
        steps: [
          {
            stepName: formData.stepName,
            stepOrder: formData.stepOrder,
            entrySourceType: ENTRY_SOURCE_TYPE_MANUAL,
            result: formData.result || undefined,
            notes: formData.notes || undefined,
            deadline: formData.deadline || undefined,
            startDate: formData.startDate || undefined,
            endDate: formData.endDate || undefined,
            place: formData.place || undefined,
          },
        ],
      });

      if (result.ok) {
        // キャッシュ無効化
        await queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.PAGINATED_ENTRY_MANAGEMENTS],
        });
        await queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.ENTRY_MANAGEMENTS],
        });
        await queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.PROGRESS_MISSIONS],
        });
        await queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.ENTRY_CALENDARS],
        });

        form.reset();

        // 作成されたステップ情報を返す
        const createdStep = result.value.steps[0];
        return {
          success: true,
          stepID: createdStep?.id,
          userEntryManagementID: createdStep?.userEntryManagementID,
        };
      }

      setErrorMessage('エントリー管理の追加に失敗しました。');
      return {success: false};
    } catch (_error) {
      setErrorMessage('エントリー管理の追加に失敗しました。');
      return {success: false};
    }
  };

  const clearMessages = () => {
    setErrorMessage(null);
  };

  return {
    form,
    handleSubmit,
    isLoading: createMutation.isLoading,
    errorMessage,
    clearMessages,
  };
};
